import { ComponentProps, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import { Divider } from "../Divider";
import { Checkbox } from "../inputs/CheckBox";
import { css, useTheme } from "@emotion/react";
import { Text } from "components/core/Text";
import { Tooltip } from "components/core/Tooltip";
import { Button } from "components/core/buttons/Button";
import { MixModal } from "components/core/modals/MixModal";
import {
  GetUserInterestTagsDocument,
  Mix_Content_Tag_Enum_Enum,
  useDeleteUserInterestTagsMutation,
  useGetUserInterestTagsQuery,
  useSessionQuery,
  useUpsertUserInterestTagsMutation,
} from "graphql/generated";
import { useWindowSize } from "hooks/useWindowSize";
import {
  MIX_CONTENT_TAG_ENUM_KOREAN,
  ORDERED_MIX_CONTENT_TAG_ENUM,
  SHINHAN_INVEST_TAG_LIST,
  TRIAL_TAG_LIST,
} from "utils/enum";

export const SelectInterestTagModal = ({
  isOpen,
  onRequestClose,
}: ComponentProps<typeof MixModal>) => {
  const theme = useTheme();
  const { isMobile } = useWindowSize();
  const router = useRouter();
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [isCompleting, setIsCompleting] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isFirstToggle, setIsFirstToggle] = useState(true);

  const { data: sessionData } = useSessionQuery();
  const userId = sessionData?.session?.me?.id;
  const userGroup = sessionData?.session?.me?.group_id;

  // 특정 페이지에서는 전용 카테고리(Trial) 섹션을 숨깁니다
  // Additionally, only show trial tags to teammint users
  const showTrialSection = useMemo(() => {
    return router.pathname !== "/" && userGroup === "teammint";
  }, [router.pathname, userGroup]);

  // Helper function to check if user has Shinhan access
  const hasShinhanAccess = useMemo(() => {
    const hasAccess = userGroup === "shinhaninvest";
    return hasAccess;
  }, [userGroup]);

  const defaultTags = useMemo(
    () =>
      ORDERED_MIX_CONTENT_TAG_ENUM.filter(
        (tag) =>
          !(SHINHAN_INVEST_TAG_LIST as string[]).includes(tag) &&
          !(TRIAL_TAG_LIST as string[]).includes(tag),
      ),
    [],
  );

  const { data: userInterestTagsData, loading: isLoadingTags } =
    useGetUserInterestTagsQuery({
      variables: { userId: userId! },
      skip: !userId, // userId가 없으면 query 실행하지 않음
    });

  const [upsertUserInterestTags, { loading: isUpsertLoading }] =
    useUpsertUserInterestTagsMutation({
      refetchQueries: [
        { query: GetUserInterestTagsDocument, variables: { userId } },
      ],
    });
  const [deleteUserInterestTags, { loading: isDeleteLoading }] =
    useDeleteUserInterestTagsMutation({
      refetchQueries: [
        { query: GetUserInterestTagsDocument, variables: { userId } },
      ],
    });

  // DB에서 조회한 사용자 관심 태그로 초기값 설정
  useEffect(() => {
    if (!isCompleting && userInterestTagsData?.mix_user_interest_tag) {
      const tagsFromDB = new Set(
        userInterestTagsData.mix_user_interest_tag.map(
          (item) => item.tag_value as string,
        ),
      );

      if (tagsFromDB.size > 0) {
        // DB에 데이터가 있으면 해당 태그들을 선택
        setSelectedTags(tagsFromDB);
        setIsFirstToggle(false);
      } else {
        // DB에 데이터가 없으면 모든 태그를 선택된 상태로 초기화
        const allTags = new Set([
          ...defaultTags,
          ...(hasShinhanAccess ? SHINHAN_INVEST_TAG_LIST : []),
          ...(showTrialSection ? TRIAL_TAG_LIST : []),
        ]);
        setSelectedTags(allTags);
        setIsFirstToggle(true);
      }
    } else if (!isCompleting && userInterestTagsData && !isLoadingTags) {
      // 쿼리는 완료되었지만 데이터가 없는 경우 (첫 방문)
      const allTags = new Set([
        ...defaultTags,
        ...(hasShinhanAccess ? SHINHAN_INVEST_TAG_LIST : []),
        ...(showTrialSection ? TRIAL_TAG_LIST : []),
      ]);
      setSelectedTags(allTags);
      setIsFirstToggle(true);
    }
  }, [
    userInterestTagsData,
    isCompleting,
    isLoadingTags,
    defaultTags,
    hasShinhanAccess,
    showTrialSection,
  ]);

  const toggleTag = async (tag: string) => {
    if (isFirstToggle && userId) {
      // 첫 토글 시: 현재 선택된 태그들(OFF한 태그 제외)만 upsert
      setIsFirstToggle(false);

      // 로컬 상태 먼저 업데이트 (OFF한 태그 제거)
      const updatedTags = new Set(selectedTags);
      updatedTags.delete(tag);
      setSelectedTags(updatedTags);

      try {
        // 업데이트된 태그들만 upsert
        if (updatedTags.size > 0) {
          const tagObjects = Array.from(updatedTags).map((tagValue, index) => ({
            user_id: userId,
            tag_value: tagValue as Mix_Content_Tag_Enum_Enum,
            priority: index + 1,
          }));

          await upsertUserInterestTags({
            variables: {
              objects: tagObjects,
            },
          });
        }
      } catch (error) {
        setIsFirstToggle(true); // 실패 시 되돌리기
        // 실패 시 로컬 상태도 되돌리기
        setSelectedTags(selectedTags);
      }
    } else {
      // 일반 토글 로직
      setSelectedTags((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(tag)) {
          newSet.delete(tag);
        } else {
          newSet.add(tag);
        }
        return newSet;
      });
    }
  };

  const toggleAllTags = (tagList: readonly string[]) => {
    const allSelected = tagList.every((tag) => selectedTags.has(tag));

    if (allSelected) {
      // 전체 해제: 선택된 태그들을 하나씩 해제
      tagList.forEach((tag) => {
        if (selectedTags.has(tag)) {
          toggleTag(tag);
        }
      });
    } else {
      // 전체 선택: 선택되지 않은 태그들을 하나씩 선택
      tagList.forEach((tag) => {
        if (!selectedTags.has(tag)) {
          toggleTag(tag);
        }
      });
    }
  };

  const isAllTagsSelected = (tagList: readonly string[]) => {
    return tagList.length > 0 && tagList.every((tag) => selectedTags.has(tag));
  };

  const isNoTagsSelected = selectedTags.size === 0 && !isFirstToggle;

  // 태그가 선택되면 tooltip 숨기기
  useEffect(() => {
    if (!isNoTagsSelected) {
      setShowTooltip(false);
    } else {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
    }
  }, [isNoTagsSelected]);

  const handleRequestClose = () => {
    if (isNoTagsSelected) {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
      return;
    }
    onRequestClose?.();
  };

  const handleComplete = async () => {
    if (!userId) {
      return;
    }

    setIsCompleting(true);

    const selectedTagsArray = Array.from(selectedTags);

    try {
      // 1. 먼저 선택되지 않은 태그들을 삭제
      await deleteUserInterestTags({
        variables: {
          userId,
          tagValues: selectedTagsArray as Mix_Content_Tag_Enum_Enum[],
        },
      });

      // 2. 선택된 태그들을 priority와 함께 upsert
      if (selectedTagsArray.length > 0) {
        const tagObjects = selectedTagsArray.map((tag, index) => ({
          user_id: userId,
          tag_value: tag as Mix_Content_Tag_Enum_Enum,
          priority: index + 1, // 1부터 시작하는 priority
        }));

        await upsertUserInterestTags({
          variables: {
            objects: tagObjects,
          },
        });
      }

      const { pathname, query } = router;
      await router.push({
        pathname,
        query: { ...query, v: new Date().getTime() },
      });

      handleRequestClose();
    } catch (error) {
      // TODO: 에러 토스트 메시지 표시
      setIsCompleting(false);
    }
  };

  const tagContainerCss = css`
    display: flex;
    flex-wrap: wrap;
    justify-content: start;
    gap: 12px;
    margin-bottom: 40px;
    max-width: 483px;
    width: 100%;
  `;

  const renderTagButtons = (tags: readonly string[]) => {
    return tags.map((tag) => (
      <Button
        key={tag}
        variant="filled"
        color={selectedTags.has(tag) ? "primary" : "grey"}
        size="medium"
        onClick={() => toggleTag(tag)}
        css={css`
          height: 27px;
          padding: 16.5px 14px 14px 14px;
          border-radius: 12px;
          font-family: "Pretendard";
          font-size: 12px;
          font-weight: 600;
          line-height: 139.9999976158142%;
          transition: all 0.2s ease;

          &:hover {
            border-color: ${theme.palette.primary.main};
            ${!selectedTags.has(tag) &&
          css`
              background-color: ${theme.palette.primary.hover};
            `}
          }

          @media (max-width: 768px) {
            padding: 16px 10px;
          }
        `}
      >
        {MIX_CONTENT_TAG_ENUM_KOREAN[tag]}
      </Button>
    ));
  };

  return (
    <MixModal isOpen={isOpen} onRequestClose={handleRequestClose}>
      <div
        css={css`
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          max-width: 600px;
          padding: 48px 40px;
          background-color: ${theme.mode === "dark"
            ? theme.palette.grey[50]
            : theme.palette.common.white.main
          };
          border-radius: 24px;
          margin: 0 24px;
          box-shadow: 0px 8px 32px 0px rgba(0, 0, 0, 0.16);

          @media (max-width: 768px) {
            max-width: 342px;
            padding: 32px 24px;
            border-radius: 20px;
          }
        `}
      >
        {/* 제목 */}
        <div
          css={css`
            text-align: center;
            margin-bottom: 32px;
          `}
        >
          <Text variant={isMobile ? "h3" : "h2"}>
            관심 주제를 선택해 주세요.
          </Text>
          <br />
          <Text
            variant={isMobile ? "body3" : "body1"}
            color="text.secondary"
            css={css`
              margin-top: 12px;
            `}
          >
            이후에도 언제든 변경할 수 있어요.
          </Text>
        </div>

        {/* 신한투자증권 전용 태그 (조건부 렌더링) */}
        {hasShinhanAccess && (
          <>
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 16px;
                margin-left: 20px;
                align-self: flex-start;
              `}
            >
              <Image
                src="/img/logo/shinhaninvest.png"
                alt="shinhaninvest"
                width={24}
                height={24}
              />
              <Text variant="button.small" color="text.primary">
                신한투자증권 전용 카테고리
              </Text>
            </div>
            <div
              css={css`
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                align-self: flex-start;
                justify-content: start;
                gap: 2px;
                margin-bottom: 8px;
                margin-left: 12px;
                cursor: pointer;
              `}
              onClick={() => toggleAllTags(SHINHAN_INVEST_TAG_LIST)}
            >
              <Checkbox
                size="medium"
                checked={isAllTagsSelected(SHINHAN_INVEST_TAG_LIST)}
              />
              <span
                css={css`
                  color: ${theme.palette.primary.main};
                `}
              >
                모든 카테고리
              </span>
            </div>
            <div css={tagContainerCss}>
              {renderTagButtons(SHINHAN_INVEST_TAG_LIST)}
            </div>
            <Divider
              css={css`
                margin-bottom: 40px;
              `}
            />
          </>
        )}

        {/* 전용 카테고리 (Trial tags) - Only shown to teammint users and not on home(/) */}
        {showTrialSection && (
          <>
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 16px;
                margin-left: 20px;
                align-self: flex-start;
              `}
            >
              <Image src="/img/logo/mirae.png" alt="mirae" width={24} height={24} />
              <Text variant="text.trial">전용 카테고리</Text>
            </div>
            <div
              css={css`
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                align-self: flex-start;
                justify-content: start;
                gap: 2px;
                margin-bottom: 8px;
                margin-left: 12px;
                cursor: pointer;
              `}
              onClick={() => toggleAllTags(TRIAL_TAG_LIST)}
            >
              <Checkbox size="medium" checked={isAllTagsSelected(TRIAL_TAG_LIST)} />
              <span
                css={css`
                  color: ${theme.palette.primary.main};
                `}
              >
                모든 카테고리
              </span>
            </div>
            <div css={tagContainerCss}>{renderTagButtons(TRIAL_TAG_LIST)}</div>
            <Divider
              css={css`
                margin-bottom: 40px;
              `}
            />
          </>
        )}

        {/* 기본 태그 (신한투자증권 일 경우 '믹스 기본 카테고리'임을 표시)*/}
        {hasShinhanAccess && (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 16px;
              margin-left: 20px;
              align-self: flex-start;
            `}
          >
            <Image src="/img/logo/mix.png" alt="shinhaninvest" width={24} height={24} />
            <Text variant="button.small" color="text.primary">
              믹스 기본 카테고리
            </Text>
          </div>
        )}
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            align-self: flex-start;
            justify-content: start;
            gap: 2px;
            margin-bottom: 8px;
            margin-left: 12px;
            cursor: pointer;
          `}
          onClick={() => toggleAllTags(defaultTags)}
        >
          <Checkbox size="medium" checked={isAllTagsSelected(defaultTags)} />
          <span
            css={css`
              color: ${theme.palette.primary.main};
            `}
          >
            모든 카테고리
          </span>
        </div>
        <div css={tagContainerCss}>{renderTagButtons(defaultTags)}</div>

        {/* 하단 버튼 */}
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            align-items: center;
            width: 100%;
          `}
        >
          <Tooltip
            placement="top"
            trigger="mouseover"
            open={showTooltip || undefined}
          >
            <Tooltip.Trigger asChild={false}>
              <Button
                variant="filled"
                color="secondary"
                size="small"
                onClick={handleComplete}
                disabled={
                  isUpsertLoading ||
                  isDeleteLoading ||
                  isLoadingTags ||
                  isCompleting ||
                  isNoTagsSelected
                }
                css={css`
                  padding: 2px 8px;
                `}
              >
                {isLoadingTags
                  ? "로딩 중..."
                  : isDeleteLoading || isUpsertLoading || isCompleting
                    ? "저장 중..."
                    : "완료"}
              </Button>
            </Tooltip.Trigger>
            {isNoTagsSelected && (
              <Tooltip.Content
                css={css`
                  max-width: 260px;
                  padding: 8px 10px;
                  border-radius: 6px;
                  font-size: 12px;
                  word-break: keep-all;
                  z-index: 1000;
                `}
              >
                카테고리를 최소 1개 이상 선택해 주세요
              </Tooltip.Content>
            )}
          </Tooltip>
        </div>
      </div>
    </MixModal>
  );
};
